import { useEffect, useRef, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import PropTypes from 'prop-types';
import { GoogleMapsOverlay } from '@deck.gl/google-maps';
import { MVTLayer } from '@deck.gl/geo-layers';
import { useGoogleMap } from '@react-google-maps/api';
import { openLayerTray } from '@actions/map-actions';
import { mapConfig } from '@constants/component-configs';
import { LAYER_TRAY } from '@constants/map-trays';

// Tiles are pre-generated up to zoom 14; deck.gl overzooms above that (vector re-render, no pixelation)
const MAX_TILE_ZOOM = 14;
// Extra pixels added to strokeWeight for the selected feature (matches figure.js highlight behaviour)
const HIGHLIGHT_EXTRA_WIDTH = 3;

// ---------------------------------------------------------------------------
// Style helpers
// ---------------------------------------------------------------------------

const hexToRgba = (color, opacity = 1) => {
  if (!color) {
    return [0, 0, 0, 0];
  }
  if (color === 'white') {
    return [255, 255, 255, Math.round((opacity ?? 1) * 255)];
  }
  const hex = color.replace('#', '');
  const full = hex.length === 3
    ? hex.split('').map(ch => ch + ch)
      .join('')
    : hex;
  return [
    parseInt(full.substring(0, 2), 16),
    parseInt(full.substring(2, 4), 16),
    parseInt(full.substring(4, 6), 16),
    Math.round((opacity ?? 1) * 255)
  ];
};

const resolveStyle = (feature, mapStyle, styleCondition) => {
  if (styleCondition && feature.properties) {
    const condValue = feature.properties[styleCondition];
    if (mapStyle && Object.prototype.hasOwnProperty.call(mapStyle, condValue)) {
      return mapStyle[condValue];
    }
  }
  return mapStyle || {};
};

const featureId = feature => feature.id ?? feature.properties?._id;

// ---------------------------------------------------------------------------
// Component
// ---------------------------------------------------------------------------

const VectorTileLayer = ({ name }) => {
  const dispatch = useDispatch();
  const map = useGoogleMap();
  const layer = useSelector(state => state.layers[name]);
  const deckRef = useRef(null);
  const [selectedId, setSelectedId] = useState(null);
  const isLayerTrayOpen = useSelector(state => {
    const trays = state.map.trays;
    if (!trays?.length) {
      return false;
    }
    const top = trays[trays.length - 1];
    return top?.trayType === LAYER_TRAY && top?.data?.type === name;
  });

  // Clear selection when the tray for this layer is closed (mirrors figure.js trayEntity behaviour)
  useEffect(() => {
    if (!isLayerTrayOpen) {
      setSelectedId(null);
    }
  }, [isLayerTrayOpen]);

  // Create/destroy the deck.gl overlay with the map's lifetime
  useEffect(() => {
    if (!map) {
      return void 0; // eslint-disable-line no-void
    }
    deckRef.current = new GoogleMapsOverlay({ layers: [], pickingRadius: 10 });
    deckRef.current.setMap(map);
    return () => {
      deckRef.current.setMap(null);
      deckRef.current = null;
    };
  }, [map]);

  // Update the MVTLayer whenever layer config or selection changes
  useEffect(() => {
    if (!deckRef.current || !layer?.tile_base_url) {
      return;
    }

    const { map_style: mapStyle, style_condition: styleCondition } = layer;
    const tileUrl = `${layer.tile_base_url}/{z}/{x}/{y}.pbf`;
    const isSelected = selectedId !== null && selectedId !== undefined;  // eslint-disable-line no-undefined

    // Glow layers: rendered beneath the main layer, only visible on the selected feature.
    // Replicates the middle + outer overlapping shapes from figure.js.
    const glowLayers = isSelected ? [
      new MVTLayer({
        id: `vtl-${name}-glow-outer`,
        data: tileUrl,
        maxZoom: MAX_TILE_ZOOM,
        pickable: false,
        stroked: true,
        filled: false,
        lineCapRounded: true,
        lineJointRounded: true,
        getLineColor: feature => featureId(feature) === selectedId
          ? hexToRgba(mapConfig.hoverStyle.outer.strokeColor, mapConfig.hoverStyle.outer.strokeOpacity)
          : [0, 0, 0, 0],
        getLineWidth: feature => featureId(feature) === selectedId
          ? mapConfig.hoverStyle.outer.strokeWeight : 0,
        lineWidthUnits: 'pixels',
        updateTriggers: { getLineColor: selectedId, getLineWidth: selectedId }
      }),
      new MVTLayer({
        id: `vtl-${name}-glow-middle`,
        data: tileUrl,
        maxZoom: MAX_TILE_ZOOM,
        pickable: false,
        stroked: true,
        filled: false,
        lineCapRounded: true,
        lineJointRounded: true,
        getLineColor: feature => featureId(feature) === selectedId
          ? hexToRgba(mapConfig.hoverStyle.middle.strokeColor, 1)
          : [0, 0, 0, 0],
        getLineWidth: feature => featureId(feature) === selectedId
          ? mapConfig.hoverStyle.middle.strokeWeight : 0,
        lineWidthUnits: 'pixels',
        updateTriggers: { getLineColor: selectedId, getLineWidth: selectedId }
      })
    ] : [];

    deckRef.current.setProps({
      layers: [
        ...glowLayers,
        new MVTLayer({
          id: `vtl-${name}`,
          data: tileUrl,
          maxZoom: MAX_TILE_ZOOM,
          pickable: true,

          getLineColor: feature => {
            const style = resolveStyle(feature, mapStyle, styleCondition);
            return hexToRgba(style.strokeColor, style.strokeOpacity ?? 1);
          },
          getLineWidth: feature => {
            const style = resolveStyle(feature, mapStyle, styleCondition);
            const base = style.strokeWeight ?? 1;
            return featureId(feature) === selectedId ? base + HIGHLIGHT_EXTRA_WIDTH : base;
          },
          lineWidthUnits: 'pixels',
          lineWidthMinPixels: 1,

          getFillColor: feature => {
            const style = resolveStyle(feature, mapStyle, styleCondition);
            const opacity = featureId(feature) === selectedId ? 0.6 : (style.fillOpacity ?? 0.3);
            return hexToRgba(style.fillColor, opacity);
          },

          getPointRadius: 4,
          pointRadiusUnits: 'pixels',

          stroked: true,
          filled: true,

          updateTriggers: {
            getLineWidth: selectedId,
            getFillColor: selectedId
          },

          onClick: ({ object }) => {
            if (!object) {
              return;
            }
            const id = featureId(object);
            if (id !== null && id !== undefined) {  // eslint-disable-line no-undefined
              setSelectedId(id);
              dispatch(openLayerTray({ id, type: name }));
            }
          }
        })
      ]
    });
  }, [layer, name, dispatch, selectedId]);

  return null;
};

VectorTileLayer.propTypes = {
  name: PropTypes.string
};

export default VectorTileLayer;
